// ============================================================================
// « ARTICLE SIMPLE » — la première des trois portes de création (relevé maquette l.996-1436).
//
// ⚠️ REPRIS SUR L'ARBRE RÉEL DE LA MAQUETTE, pas sur des fragments. Mes premières versions extrayaient
// des libellés au grep puis devinaient la mise en page : le résultat ne ressemblait à rien de ce qui
// est dessiné. La forme vient d'UNE SEULE grille `auto-fit minmax(220px,1fr)` où les champs larges
// prennent `grid-column: 1/-1` — pas d'une pile de blocs à intertitres.
//
// ⚠️ AUCUNE CAPACITÉ SERVEUR N'A ÉTÉ AJOUTÉE : `ArticleInput` porte déjà les champs, la création de
// déclinaison ALLOUE les gencodes manquants, et la carte de différences écrit le reste. Une route
// dédiée aurait été un second chemin d'écriture à côté du producteur — donc un chemin qui, un jour,
// oublierait de journaliser et ne descendrait à aucune caisse.
//
// ⚠️ « SIMPLE » NE VEUT PAS DIRE « UNE SEULE DÉCLINAISON ». Le libellé de vide de la grille de tailles
// porte toute la logique de l'écran : « Aucune · une seule déclinaison ». En choisir une fait
// apparaître un code-barres PAR TAILLE — c'est ainsi qu'on crée une série sans la gamme complète.
// ============================================================================
import { useCallback, useEffect, useMemo, useState } from 'react';
import { confirm, Modal, Picker, showToast, Switch, Trans } from '@orgazone/ui-web';
import { formatMoney, formatMinor, parseMinor, toInputValue } from '@orgazone/money-format';
import { achatTheoriqueCorner, coeffFromPrices, fineEnCentimes, purchaseFromCoeff, ttcFromCoeff } from './prix-derive';
import { sizeAxes } from './article-gamme-matrice';
import { ArticleCodeNotice, leaveForHolder, useArticleCodeCheck } from './article-code-check';
import { api, type ArticleCodeHolder, type ArticleFacet, type ArticleFacets, type SecondAxisOption, type SizeGridOption } from '../api';
import { useOfferedFacets } from './article-offres';
import { t } from '../i18n';
import { fieldLabel } from '../vocabulary';
import { ITEM_KIND_TEXT } from '../article-flags';
import { creationVatRateBp, useVatDefaults, vatRateOptions } from '../vat-rates';

/** Ce que l'écran collecte — miroir des champs de la maquette, dans son ordre. */
interface Draft {
  nom: string;
  code: string;
  secteur: string;
  famille: string;
  brand: string;
  supplier: string;
  pa: string;
  coeff: string;
  pv: string;
  corner: boolean;
  saison: string;
  tva: string;
  web: boolean;
  /**
   * UNE PRESTATION (retouche, service) plutôt qu'une marchandise — ni taille, ni stock, ni coût d'achat.
   * ⚠️ SEULEMENT CES DEUX NATURES À LA CRÉATION : un chèque cadeau créé comme article se vendrait en caisse sans émettre
   *    de titre utilisable. La nature « chèque cadeau ou acompte » se pose sur la fiche, pour marquer un article repris.
   */
  service: boolean;
  grid: string;
  /**
   * LA GRILLE DU SECOND AXE (longueurs, bonnets…) — vide = un seul axe.
   * ⚠️ ELLE SE CHOISIT, elle ne se déduit pas de la grille de tailles : deux modèles d'une même grille
   * n'ont aucune raison d'avoir le même second axe — un pantalon a des longueurs, un tee-shirt non.
   */
  grid2: string;
  /** Un code-barres PAR TAILLE, quand une grille est choisie. Vide = taille non créée. */
  codes: Record<string, string>;
}

const VIDE: Draft = {
  nom: '', code: '', secteur: '', famille: '', brand: '', supplier: '',
  pa: '', coeff: '', pv: '', corner: false,
  // ⚠️ AUCUN TAUX D'OFFICE : il se pré-remplit de celui de la famille, sinon du magasin, une fois lus (`creationVatRateBp`).
  saison: '', tva: '', web: false, service: false, grid: '', grid2: '', codes: {},
};

export function NouvelArticleSimple({ open, onClose, offres, onCree, onOpenArticle }: {
  open: boolean;
  onClose: () => void;
  /** Les valeurs EXISTANTES du magasin — les sélecteurs n'inventent aucune option. */
  offres: ArticleFacets | null;
  /** Rechargement de la liste, une fois la création confirmée par le serveur. */
  onCree: () => void;
  /** Ouvre la fiche de l'article qui porte déjà le code saisi, sur ses déclinaisons. */
  onOpenArticle?: (holder: ArticleCodeHolder) => void;
}) {
  const [d, setD] = useState<Draft>(VIDE);
  /** L'opérateur a choisi le taux lui-même : changer de famille ne le remplace plus. */
  const [tvaTouched, setTvaTouched] = useState(false);
  const vatDefaults = useVatDefaults(open);
  const [writing, setWriting] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  /** Le nom de l'article CRÉÉ — présent = on est sur l'écran de fin. */
  const [cree, setCree] = useState<string | null>(null);
  /** Les grilles du magasin — leur PROPRE route : une grille n'est pas une facette. */
  const [grids, setGrids] = useState<SizeGridOption[]>([]);
  /** Les grilles SECONDAIRES du magasin — la liste dans laquelle le second axe se choisit. */
  const [dimensions, setDimensions] = useState<SecondAxisOption[]>([]);

  /**
   * LA CLÉ DE REJEU DE CETTE OUVERTURE — la même à chaque « Créer » : la création relancée après une réponse perdue ou un
   * échec à mi-chemin (déclinaisons écrites, champs non) retrouve l'article qu'elle a déjà créé au lieu de le refuser.
   */
  const [runKey, setRunKey] = useState('');

  // Chaque ouverture repart d'un brouillon neuf : rouvrir sur les champs du précédent ferait créer
  // deux fois le même article à qui clique vite.
  useEffect(() => {
    if (!open) return;
    setD(VIDE); setTvaTouched(false); setCree(null); setFailure(null); setRunKey(`art-simple:${crypto.randomUUID()}`);
  }, [open]);

  // ⚠️ LE TAUX SUIT LA FAMILLE tant que l'opérateur ne l'a pas choisi : celui qu'elle porte, sinon celui du magasin.
  useEffect(() => {
    if (tvaTouched || !vatDefaults) return;
    const bp = creationVatRateBp(vatDefaults, d.famille);
    setD((p) => ({ ...p, tva: bp === null ? '' : String(bp) }));
  }, [vatDefaults, d.famille, tvaTouched]);

  // ⚠️ LUES À L'OUVERTURE et non au montage : la modale vit toute la session, et le magasin actif peut
  // changer entre deux créations.
  useEffect(() => {
    if (!open) return;
    let alive = true;
    api.articleSizeGrids()
      .then((r) => { if (alive) { setGrids(r.grids); setDimensions(r.dimensions); } })
      // ⚠️ Best-effort ASSUMÉ : la grille est FACULTATIVE, son absence ne doit pas empêcher de créer.
      .catch(() => { if (alive) { setGrids([]); setDimensions([]); } });
    return () => { alive = false; };
  }, [open]);

  const set = <K extends keyof Draft>(k: K, v: Draft[K]) => setD((p) => ({ ...p, [k]: v }));
  const { offered, createAndOffer } = useOfferedFacets(offres);

  /**
   * LE CODE ARTICLE — saisi, sinon le premier LIBRE à partir de la désignation (`proposeArticleCode`, la règle du cœur
   * partagée avec l'import, puis `-2`, `-3`… s'il est porté).
   * ⚠️ UN ARTICLE NEUF NE SE RATTACHE JAMAIS À UN AUTRE : sous un code porté, la création s'ajouterait à cet article et ses
   *    champs écraseraient ceux de l'original. Rattacher se fait depuis la fiche de l'article, que l'avis propose d'ouvrir.
   */
  const codeCheck = useArticleCodeCheck(open, d.nom, d.code);
  const effectiveCode = codeCheck.effective;
  /** Les tailles de la grille choisie — vide tant qu'aucune n'est retenue. */
  const sizes = useMemo(
    () => (d.grid ? grids.find((g) => g.name === d.grid)?.sizes ?? [] : []),
    [d.grid, grids],
  );
  /**
   * Le second axe CHOISI — `null` tant qu'aucun ne l'est.
   * ⚠️ LE DESSIN L'AVAIT VU JUSTE : il offrait « Longueurs — facultatif · dimension 2 » PAR ARTICLE, et
   * le code s'en écartait parce que le modèle d'alors rattachait la dimension à la GRILLE. Le
   * rattachement ayant rejoint l'article, la divergence disparaît — l'écran suit à nouveau la maquette.
   */
  const dimension = useMemo(
    () => (d.grid2 ? dimensions.find((x) => x.name === d.grid2) ?? null : null),
    [d.grid2, dimensions],
  );
  /** Les colonnes : chaque taille, croisée avec chaque valeur de la dimension — libellé « 40/32 ». */
  const axes = useMemo(() => sizeAxes(sizes, dimension), [sizes, dimension]);

  const pv = parseMinor(d.pv, { scale: 2 });
  const pa = parseMinor(d.pa, { scale: 4 });
  // « Quatre champs » : désignation, famille, prix de vente, TVA. Le reste se complète depuis la fiche.
  const complet = d.nom.trim() !== '' && d.famille !== '' && pv.state === 'ok' && d.tva !== '' && !codeCheck.blocks;

  const create = useCallback(async () => {
    if (!complet || writing) return;
    setWriting(true);
    setFailure(null);
    try {
      const key = `${runKey}:${effectiveCode}`;
      // ⚠️ UNE DÉCLINAISON PAR TAILLE RENSEIGNÉE — et une seule, sans grille. Le serveur ALLOUE les
      // gencodes manquants : le navigateur n'en invente jamais, sinon deux onglets ouverts
      // produiraient deux articles là où l'opérateur en voit un.
      // ⚠️ LES DEUX AXES PARTENT SÉPARÉS — le libellé « 40/32 » n'est qu'une colonne d'écran ; le
      //    serveur stocke `size` et `size2` dans deux colonnes, et c'est ainsi qu'il les reçoit.
      const variantes = axes.length > 0
        ? axes
            .filter((a) => (d.codes[a.label] ?? '').trim() !== '')
            .map((a) => ({ color: null, size: a.size, size2: a.size2, gencode: d.codes[a.label]!.trim() }))
        : [{ color: null, size: null, gencode: null }];
      if (variantes.length === 0) {
        throw new Error(t('Aucune taille renseignée — une taille laissée vide n’est pas créée.'));
      }
      // ⚠️ LA GRILLE PART AVEC LES DÉCLINAISONS : le serveur résout chaque taille en ligne de SA grille,
      //    et refuse une taille sans grille. La poser après, avec les champs, arrivait trop tard.
      // ⚠️ PAS DE SECOND AXE SANS GRILLE : il n'a rien à croiser (défense en profondeur, l'écran le vide déjà).
      // ⚠️ `newModel` : le serveur refuse un code déjà porté — c'est la règle, l'avis sous le champ n'en est que l'annonce.
      await api.createArticleVariants(effectiveCode, variantes, key, d.grid || null, d.grid ? d.grid2 || null : null, { newModel: true });
      // Les CHAMPS passent par la carte de différences de la fiche : la MÊME route que l'édition. Une
      // seconde porte d'écriture finirait par diverger de celle qu'on relit.
      await api.setArticleFields(effectiveCode, {
        name: d.nom.trim(),
        family: d.famille,
        ...(d.secteur ? { sector: d.secteur } : {}),
        ...(d.brand ? { brand: d.brand } : {}),
        ...(d.supplier ? { supplier: d.supplier } : {}),
        ...(d.saison ? { season: d.saison } : {}),
        ...(d.grid ? { sizeGrid: d.grid } : {}),
        salePriceInclTaxCents: String(pv.state === 'ok' ? pv.minor : 0),
        // ⚠️ UNE PRESTATION N'ENVOIE PAS DE PRIX D'ACHAT : il ne compterait nulle part (sa marge déduit des marchandises).
        ...(pa.state === 'ok' && !d.service ? { purchasePriceExclTaxFine: String(pa.minor) } : {}),
        vatRateBp: d.tva,
        purchaseRegime: d.corner ? 'corner' : 'firm',
        isWebActive: d.web,
        // ⚠️ LA NATURE ET LE STOCK PARTENT ENSEMBLE : le serveur refuse une prestation suivie en stock.
        ...(d.service ? { itemKind: 'service', isStockManaged: false } : {}),
      }, `${key}:champs`);
      setCree(d.nom.trim());
      onCree();
    } catch (e) {
      // ⚠️ LE MOTIF RÉEL : « code-barres déjà pris » et « famille inconnue » appellent deux gestes
      // différents, et un « échec » générique laisse chercher lequel.
      setFailure(e instanceof Error ? e.message : String(e));
    } finally {
      setWriting(false);
    }
  }, [complet, writing, d, effectiveCode, runKey, axes, pv, pa, onCree]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      // 600 px au relevé — `lg` (620) est le cran de la rampe qui s'en approche.
      width="lg"
      title={cree ?? (d.nom.trim() || t('Nouvel article'))}
      subtitle={t('Article simple · quatre champs suffisent')}
      icon="box"
      tone="teal"
      // ⚠️ DENSITÉ `ample`, LA VALEUR PAR DÉFAUT — et surtout PAS `fiche`. Les deux existent, et
      //    `fiche` est celle de la FICHE article : tuile de 52 px, titre de 19. Le relevé de cette
      //    modale-ci donne 38 et 16,5 — exactement `ample`. C’est ce qui décalait tout l’en-tête.
      // ⚠️ Une modale ne se ferme pas PENDANT l'écriture : on ne saurait pas dire si l'article existe.
      blockClose={() => writing}
      footer={cree ? undefined : (
        <>
          <button type="button" className="btn-ghost" onClick={onClose} disabled={writing}>{t('Annuler')}</button>
          <div style={{ flex: 1 }} />
          {/* ⚠️ `aria-disabled` ET NON `disabled` : un bouton désactivé n'est pas survolable, donc la
              raison de son refus ne s'affiche jamais. Il reste focalisable et DIT ce qui manque. */}
          <button
            type="button" className="btn-cta" aria-disabled={!complet || writing}
            title={complet ? undefined : codeCheck.blocks
              ? (codeCheck.checking ? t('Vérification du code article…') : t('Ce code article est déjà porté : prenez le code proposé, ou ajoutez la déclinaison depuis la fiche de l’article.'))
              : t('Désignation, famille, prix de vente et TVA sont nécessaires.')}
            onClick={() => { if (complet && !writing) void create(); }}
          >{writing ? t('Création…') : t('Créer l’article')}</button>
        </>
      )}
    >
      {cree ? <Fin nom={cree} onSuivant={() => { setD(VIDE); setCree(null); }} onListe={onClose} /> : (
        <>
          {failure !== null && (
            <p role="alert" style={{
              margin: '0 0 14px', padding: '11px 13px', borderRadius: 'var(--r-sm)',
              background: 'var(--rose-bg)', border: '1px solid var(--rose-fg)',
              fontSize: '12.5px', color: 'var(--ink-800)', lineHeight: 1.5,
            }}>{failure}</p>
          )}

          {/* ⚠️ UNE SEULE GRILLE POUR TOUT LE CORPS (relevé l.1020) — c'est elle qui donne sa forme à
              l'écran : les champs larges y prennent `grid-column: 1/-1`, les autres se rangent en
              colonnes de 220 px. Une pile de blocs à intertitres, comme j'avais fait, ne ressemble à
              rien de ce qui est dessiné. */}
          <Grid>
            <FormRow large label={fieldLabel('name')}>
              <Saisie value={d.nom} onChange={(v) => set('nom', v)} placeholder={t('Chemise oxford blanche')} />
            </FormRow>

            <FormRow label={fieldLabel('articleCode')}>
              <Saisie value={d.code} onChange={(v) => set('code', v)} placeholder={codeCheck.proposed} />
              <ArticleCodeNotice check={codeCheck} typed={d.code} onTake={(c) => set('code', c)}
                onOpenHolder={onOpenArticle ? (h) => { void leaveForHolder(h, onClose, onOpenArticle); } : undefined} />
            </FormRow>

            <Selecteur id="sect" label={fieldLabel('sector')} facette="sect"
              valeur={d.secteur} onChange={(v) => set('secteur', v)} offres={offered} onCreateValue={createAndOffer} />
            <Selecteur id="fam" label={fieldLabel('family')} facette="fam"
              valeur={d.famille} onChange={(v) => set('famille', v)} offres={offered} onCreateValue={createAndOffer} />
            <Selecteur id="marq" label={fieldLabel('brand')} facette="marq"
              valeur={d.brand} onChange={(v) => set('brand', v)} offres={offered} onCreateValue={createAndOffer} />
            <Selecteur id="sup" label={fieldLabel('supplier')} facette="sup"
              valeur={d.supplier} onChange={(v) => set('supplier', v)} offres={offered} onCreateValue={createAndOffer} />

            <CartePrix d={d} set={set} />

            <Selecteur id="sais" label={fieldLabel('season')} facette="sais"
              valeur={d.saison} onChange={(v) => set('saison', v)} offres={offered} onCreateValue={createAndOffer} />

            <div style={{ minWidth: 0 }}>
              <Picker id="bo-new-tva" variant="field" label={fieldLabel('vatRate')}
                options={vatRateOptions(vatDefaults?.offeredRatesBp ?? null, d.tva === '' ? null : Number(d.tva))}
                value={d.tva} onChange={(v) => { setTvaTouched(true); set('tva', v ?? ''); }} clearLabel="" allLabel="" />
            </div>

            {/* ⚠️ FRÈRE de la cellule TVA et PLEINE LARGEUR — pas un enfant de celle-ci. C'est un
                DRAPEAU de l'article, au même titre que « remises interdites », et le relevé lui donne sa
                propre rangée bordée : ni case à cocher native, ni explication rejetée hors de la rangée. */}
            <Switch
              layout="card" style={{ gridColumn: '1/-1' }}
              checked={d.web} onChange={(v) => set('web', v)}
              label={t('Diffusé sur le site internet')}
              hint={t('Drapeau de l’article, comme « remises interdites ».')}
            />

            {/* ⚠️ UNE PRESTATION N'A NI TAILLE NI GRILLE : l'activer vide les deux axes, qui deviennent indisponibles. */}
            <Switch
              layout="card" style={{ gridColumn: '1/-1' }}
              checked={d.service} onChange={(v) => { set('service', v); if (v) { set('grid', ''); set('grid2', ''); set('codes', {}); } }}
              label={t(ITEM_KIND_TEXT.service.label)}
              hint={t(ITEM_KIND_TEXT.service.help)}
            />

            {/* ⚠️ LES DEUX AXES CÔTE À CÔTE, TOUJOURS AFFICHÉS (maquette, décision de l'utilisateur) : on voit
                d'emblée tout ce que l'article peut être. Le libellé de vide de la grille porte la logique de
                l'écran : « Sans · une seule déclinaison » — c'est là qu'« article simple » bascule en série. */}
            <div style={{ minWidth: 0 }}>
              <Picker
                id="bo-new-grille" variant="field" label={t('{v} — facultatif', { v: fieldLabel('sizeGrid') })}
                clearLabel={t('Sans · une seule déclinaison')} allLabel={t('Sans · une seule déclinaison')}
                // La grille se choisit sur ce qu'elle FAIT : « 6 tailles × 3 longueurs » dit combien de codes suivront.
                options={grids.map((g) => ({ value: g.name, label: `${g.name} · ${t('{n} taille', { n: g.sizes.length })}` }))}
                unavailable={grids.length === 0 || d.service}
                unavailableReason={d.service ? t('Une prestation n’a ni {taille} ni {coloris}.', { taille: fieldLabel('size').toLowerCase(), coloris: fieldLabel('color').toLowerCase() }) : undefined}
                // ⚠️ VIDER LA GRILLE VIDE LE SECOND AXE : gardé en mémoire, il partait à la création sans rien à croiser.
                value={d.grid} onChange={(v) => { set('grid', v ?? ''); if (!v) set('grid2', ''); }}
              />
            </div>

            {/* ⚠️ LE SECOND AXE EST UN CHOIX DE L'ARTICLE, pas une propriété de la grille (« Longueurs — facultatif
                · dimension 2 »). ⚠️ VISIBLE MAIS INACTIF TANT QU'AUCUNE GRILLE N'EST CHOISIE, et il le dit : sans
                tailles il n'y a rien à croiser, et un choix sans effet serait un choix qui ment. */}
            <div style={{ minWidth: 0 }}>
              <Picker
                id="bo-new-grille2" variant="field" label={t('{v} — facultatif', { v: fieldLabel('sizeGrid2') })}
                clearLabel={t('Sans · un seul axe')} allLabel={t('Sans · un seul axe')}
                options={dimensions.map((x) => ({
                  value: x.name,
                  label: `${x.name} · ${t('{v} ({champ})', { v: x.values.length, champ: x.label.toLowerCase() })}`,
                }))}
                unavailable={d.grid === '' || dimensions.length === 0 || d.service}
                unavailableReason={d.service ? t('Une prestation n’a ni {taille} ni {coloris}.', { taille: fieldLabel('size').toLowerCase(), coloris: fieldLabel('color').toLowerCase() }) : d.grid === ''
                  ? t('Choisissez d’abord la grille de tailles : le second axe se croise avec elle.')
                  : t('Aucune grille de second axe au référentiel de ce magasin — créez-la au référentiel des grilles.')}
                value={d.grid2} onChange={(v) => set('grid2', v ?? '')}
              />
            </div>
          </Grid>

          {/* ⚠️ UNE GRILLE VIDE SE DIT. Depuis que les grilles se créent au référentiel, on peut en
              choisir une qui n’a pas encore ses tailles : le bloc de codes-barres ne s’affichait alors
              pas, et l’article partait en UNE seule déclinaison sans taille — silencieusement, alors
              que l’opérateur venait de choisir une grille pour en avoir plusieurs. */}
          {d.grid !== '' && sizes.length === 0 && (
            <div style={{
              marginTop: 14, padding: '11px 13px', borderRadius: 'var(--r-sm)',
              background: 'var(--amber-bg)', border: '1px solid var(--amber)',
              fontSize: '12.5px', color: 'var(--ink-800)', lineHeight: 1.55,
            }}>
              <Trans k='La grille <1>{grid}</1> n’a encore aucune taille : l’article sera créé en une seule déclinaison, sans taille. Ajoutez-lui ses tailles dans <2>Référentiels › Grilles de tailles</2>, puis revenez ici.' vars={{ grid: d.grid }} parts={[(x) => <strong>{x}</strong>, (x) => <strong>{x}</strong>]} /></div>
          )}

          {sizes.length > 0 && (
            <CodesBySize
              sizes={axes.map((a) => a.label)}
              codes={d.codes}
              onCode={(size, v) => setD((p) => ({ ...p, codes: { ...p.codes, [size]: v } }))}
              onCodes={(maj) => setD((p) => ({ ...p, codes: { ...p.codes, ...maj } }))}
            />
          )}

          {/* ⚠️ RELEVÉE MOT POUR MOT (l.1120) : c'est elle qui rend les champs absents ACCEPTABLES.
              Sans elle, un formulaire court laisse croire qu'on a oublié quelque chose. */}
          <div style={{
            marginTop: 16, padding: '13px 15px', borderRadius: 'var(--r-sm)',
            background: 'var(--ink-50)', border: '1px solid var(--ink-200)',
            fontSize: '12.5px', color: 'var(--ink-600)', lineHeight: 1.55,
          }}>
            {/* ⚠️ « COEFFICIENT » A QUITTÉ CETTE LISTE (revue 2026-08-25) : le lot qui a branché la
                lecture de la clause contractuelle vient de rendre cette phrase fausse en régime
                corner — l'étape Prix affiche désormais le vrai coefficient du fournisseur, et le PA
                théorique qui en découle. Un texte qui annonce N/C juste au-dessus d'un nombre réel
                fait douter du nombre. */}
            <Trans k='Poids, origine et douane seront marqués <1>N/C</1> — non communiqués. L’article est vendable sans eux, et sa fiche les réclamera quand vous y passerez.' parts={[(x) => <strong style={{ color: 'var(--ink-900)' }}>{x}</strong>]} /></div>
        </>
      )}
    </Modal>
  );
}

/**
 * LA CARTE PRIX — bordée, pleine largeur (relevé l.1097).
 * ⚠️ ELLE EST ENCADRÉE, ET CE N'EST PAS DE L'ORNEMENT : elle réunit trois montants qui n'ont de sens
 * qu'ENSEMBLE. Hors cadre, le coefficient flottait entre deux champs sans rapport, et rien ne disait
 * qu'il pilotait le prix d'à côté.
 */
/**
 * LA CLAUSE CONTRACTUELLE DU FOURNISSEUR — lue, jamais figée.
 *
 * ⚠️ CE HOOK REMPLACE DEUX « N/C » ÉCRITS EN DUR (correctif d'audit 2026-08-25). Ils étaient justifiés
 * par « aucune source n'écrit `supplier.coefficient_bp` » — périmé : Référentiels › Fournisseurs &
 * marques l'écrit, la route le sert, et l'assistant GAMME le lit déjà de cette exacte façon, dans un
 * écran qui collecte le fournisseur comme celui-ci. Un opérateur voyait donc « N/C » sur une clause
 * posée la veille, et partait la ressaisir — ou basculait en achat ferme pour obtenir un chiffre.
 * ⚠️ TROIS ÉTATS, PAS DEUX : `undefined` = on lit (on n'affiche rien plutôt qu'un « N/C » prématuré,
 * qui serait faux une demi-seconde), `null` = il n'y en a pas, un nombre = la clause.
 */
function useContractualCoefficient(corner: boolean, supplier: string): number | null | undefined {
  const [bp, setBp] = useState<number | null | undefined>(null);
  useEffect(() => {
    const nom = supplier.trim();
    if (!corner || nom === '') { setBp(null); return; }
    let vivant = true;
    setBp(undefined);
    api.supplierCoefficient(nom)
      // ⚠️ Best-effort ASSUMÉ : sans clause lisible l'écran retombe sur « N/C », qui reste la vérité la
      //    plus sûre — il n'invente aucun coefficient. Un échec de lecture ne doit pas fermer l'assistant.
      .then((r) => { if (vivant) setBp(r.coefficientBp); })
      .catch(() => { if (vivant) setBp(null); });
    return () => { vivant = false; };
  }, [corner, supplier]);
  return bp;
}

function CartePrix({ d, set }: {
  d: Draft;
  set: <K extends keyof Draft>(k: K, v: Draft[K]) => void;
}) {
  const coeffBp = useContractualCoefficient(d.corner, d.supplier);
  /** Le coût que la caisse retiendra — dérivé de la CLAUSE, jamais d'un prix d'achat saisi. */
  const paTheoriqueFine = useMemo(() => {
    if (!d.corner || coeffBp == null) return null;
    const pv = parseMinor(d.pv, { scale: 2 });
    if (pv.state !== 'ok' || pv.minor <= 0) return null;
    return achatTheoriqueCorner(pv.minor, coeffBp);
  }, [d.corner, d.pv, coeffBp]);
  /** Une frappe dans un champ de prix, et ce qu'elle recalcule aux deux autres (`priceFieldPatch`). */
  const applyPrice = (field: 'pa' | 'coeff' | 'pv', v: string) => {
    const patch = priceFieldPatch(field, v, d);
    for (const k of Object.keys(patch) as ('pa' | 'coeff' | 'pv')[]) set(k, patch[k]!);
  };
  return (
    <div style={{
      gridColumn: '1/-1', border: '1px solid var(--ink-200)',
      borderRadius: 'var(--r-sm)', padding: '13px 14px',
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        gap: 10, flexWrap: 'wrap', marginBottom: 12,
      }}>
        <span style={{ fontSize: '12.5px', fontWeight: 700, color: 'var(--ink-900)' }}>{t('Prix')}</span>
        {/* ⚠️ UN SEGMENTÉ, PAS UNE CASE À COCHER : deux régimes EXCLUSIFS se choisissent, ils ne
            s'activent pas. Une case laisse croire à une option qu'on ajoute, alors que « Normal » est
            un état à part entière — celui de la quasi-totalité du stock. */}
        <div style={{
          display: 'flex', padding: 3, gap: 3, borderRadius: 'var(--r-ctrl)',
          background: 'var(--ink-50)', border: '1px solid var(--ink-200)',
        }}>
          {([[false, t('Normal')], [true, t('Corner — PA calculé à la vente')]] as const).map(([v, l]) => (
            <button
              key={l} type="button" aria-pressed={d.corner === v} onClick={() => set('corner', v)}
              style={{
                height: 30, padding: '0 12px', border: 'none', borderRadius: 'var(--r-xs)',
                cursor: 'pointer', fontSize: '12.5px', fontWeight: 700, whiteSpace: 'nowrap',
                background: d.corner === v ? 'var(--paper)' : 'transparent',
                color: d.corner === v ? 'var(--ink-900)' : 'var(--ink-500)',
                boxShadow: d.corner === v ? 'var(--sh-sm)' : undefined,
              }}
            >{l}</button>
          ))}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 13 }}>
        {d.corner ? (
          <>
            <FormRow label={fieldLabel('salePriceInclTax')}>
              <Saisie value={d.pv} onChange={(v) => set('pv', v)} placeholder="69,00" chiffres />
            </FormRow>
            {/* ⚠️ LU CHEZ LE FOURNISSEUR, comme dans l'assistant Gamme et dans la fiche — jamais figé.
                Le PA théorique en DÉCOULE (`achatTheoriqueCorner`) et ne se saisit pas : en corner c'est
                la clause qui commande le coût, et la caisse divisera par elle à chaque vente. */}
            <FormRow label={t('Coefficient contractuel')}>
              <Fige valeur={coeffBp === undefined ? '…' : coeffBp === null ? 'N/C'
                : formatMinor(coeffBp, { scale: 4, displayScale: 2 })} />
            </FormRow>
            <FormRow label={t('PA théorique')}>
              <Fige valeur={paTheoriqueFine === null ? 'N/C'
                : formatMoney(fineEnCentimes(paTheoriqueFine), { thousandsSep: ' ' })} />
            </FormRow>
          </>
        ) : d.service ? (
          // ⚠️ UNE PRESTATION N'A NI COÛT D'ACHAT NI COEFFICIENT : sa marge est son prix hors taxe. Offrir les deux champs
          //    ferait saisir un coût qu'aucune marge ne lirait.
          <FormRow label={fieldLabel('salePriceInclTax')}>
            <Saisie value={d.pv} onChange={(v) => set('pv', v)} placeholder="69,00" chiffres />
          </FormRow>
        ) : (
          <>
            {/* ⚠️ « HT » EST DANS LE LIBELLÉ, PAS DANS LE MOT DU MAGASIN : le vocabulaire nomme le champ
                (« Prix d’achat »), le libellé dit qu'on le saisit hors taxes — sans lui, un TTC tapé là
                fausserait le coefficient et la marge, sans erreur. */}
            <FormRow label={t('{v} HT — facultatif', { v: fieldLabel('purchasePriceExclTax') })}>
              <Saisie value={d.pa} onChange={(v) => applyPrice('pa', v)} placeholder="24,00" chiffres />
            </FormRow>
            <FormRow label={t('Coefficient')}>
              <Saisie value={d.coeff} onChange={(v) => applyPrice('coeff', v)} placeholder="2,88" chiffres />
            </FormRow>
            <FormRow label={fieldLabel('salePriceInclTax')}>
              <Saisie value={d.pv} onChange={(v) => applyPrice('pv', v)} placeholder="69,00" chiffres />
            </FormRow>
          </>
        )}
      </div>

      {/* ⚠️ SANS CETTE PHRASE, le coefficient a l'air d'un champ qu'on remplit pour rien : personne ne
          devine qu'il RECALCULE le prix de vente. */}
      {/* ⚠️ L'AIDE RENVOYAIT VERS UNE « FICHE FOURNISSEUR » QUI N'A JAMAIS EXISTÉ (audit 2026-08-25).
          La correction avait été faite dans l'onglet Prix — « posé aux Référentiels » — et jamais
          reportée ici : l'opérateur partait chercher un écran absent pour une donnée déjà en base. */}
      <Aide>{d.service && !d.corner
        ? t('Une prestation n’a ni coût d’achat ni coefficient : sa marge est son prix hors taxe.')
        : d.corner
          ? t('Le coefficient vient du contrat fournisseur — il se pose dans Référentiels › Fournisseurs & marques. Chaque vente calcule ensuite le PA réel depuis le prix encaissé.')
          : t('Saisir un coef recalcule le PV depuis le PA ; saisir un PV recalcule le coef.')}</Aide>
    </div>
  );
}

/**
 * UN CODE-BARRES PAR TAILLE — le bloc qui apparaît dès qu'une grille est choisie (relevé l.1105).
 *
 * ⚠️ UNE TAILLE LAISSÉE VIDE N'EST PAS CRÉÉE, et c'est dit en pied : c'est ce qui permet de ne
 * commander que trois tailles sur huit sans sortir de l'écran.
 *
 * ⚠️ « GÉNÉRER » ALLOUE POUR DE VRAI, côté serveur, avant même que l'article existe. Le code affiché
 * est celui qui sera écrit — il s'imprime sur l'étiquette tout de suite. Le prix de ce confort est
 * qu'un formulaire abandonné brûle ses numéros : la séquence est monotone, pas sans trou, et rien
 * n'audite la continuité des codes-barres (contrairement aux séquences fiscales, §30.7.1).
 *
 * ⚠️ DIVERGENCE ASSUMÉE À LA MAQUETTE : elle régénère TOUT sans rien demander, ce qui écraserait sans
 * retour des codes fournisseur déjà scannés. Le bouton garde son sens — il régénère bien tout —, mais
 * il DEMANDE quand il s'apprête à en détruire. Le prototype ne pouvait pas rencontrer le cas : ses
 * codes étaient fictifs.
 */
function CodesBySize({ sizes, codes, onCode, onCodes }: {
  sizes: readonly string[];
  codes: Record<string, string>;
  onCode: (taille: string, valeur: string) => void;
  /** Pose plusieurs codes d'un coup : une allocation rend un LOT, pas une valeur. */
  onCodes: (maj: Record<string, string>) => void;
}) {
  const [liveCount, setEnCours] = useState(false);
  const remplis = sizes.filter((size) => (codes[size] ?? '').trim() !== '').length;

  const allouer = useCallback(async (targets: readonly string[]) => {
    if (targets.length === 0 || liveCount) return;
    setEnCours(true);
    try {
      const { gencodes } = await api.allocateGencodes(targets.length);
      // ⚠️ ON NE POSE PAS UN LOT INCOMPLET : à moitié appliqué, il laisserait croire que les tailles
      // restantes ont été oubliées, alors que c'est l'allocation qui n'a pas tenu.
      if (gencodes.length !== targets.length) {
        throw new Error(t('{n} codes reçus pour {demandes}.', { n: gencodes.length, demandes: t('{n} demandé', { n: targets.length }) }));
      }
      const maj: Record<string, string> = {};
      targets.forEach((size, i) => { maj[size] = gencodes[i]!; });
      onCodes(maj);
    } catch (e) {
      // ⚠️ LE MOTIF RÉEL : « ce magasin n'a pas de radical EAN » se corrige depuis la Console, pas en
      // recliquant. Un « échec » générique enverrait recliquer.
      showToast(e instanceof Error ? e.message : String(e), 'err');
    } finally {
      setEnCours(false);
    }
  }, [liveCount, onCodes]);

  const toutGenerer = useCallback(async () => {
    const overwritten = sizes.filter((size) => (codes[size] ?? '').trim() !== '').length;
    if (overwritten > 0) {
      const accord = await confirm({
        title: t('Remplacer {n} code déjà renseigné ?', { n: overwritten }),
        message: t('Les codes déjà saisis ou scannés seront remplacés par des codes internes du magasin. Un code fournisseur perdu ici devra être rescanné.'),
        confirmLabel: t('Tout régénérer'),
      });
      if (!accord) return;
    }
    await allouer(sizes);
  }, [sizes, codes, allouer]);

  // ⚠️ « Scanner » NE FAIT QUE POSER LE CURSEUR sur la première case vide — et c'est déjà tout ce dont
  // une douchette a besoin, l'hôte de navigation enchaînant les champs au retour chariot. Un « mode
  // scan » qui prétendrait faire davantage n'aurait rien de plus à faire.
  const amorcerScan = useCallback(() => {
    const target = sizes.find((size) => (codes[size] ?? '').trim() === '') ?? sizes[0];
    if (target === undefined) return;
    document.querySelector<HTMLInputElement>(`[data-scansize="${CSS.escape(target)}"]`)?.focus();
  }, [sizes, codes]);

  return (
    <div style={{
      marginTop: 18, border: '1px solid var(--ink-200)', borderRadius: 'var(--r-sm)', overflow: 'hidden',
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px', flexWrap: 'wrap',
        background: 'var(--ink-50)', borderBottom: '1px solid var(--ink-200)',
      }}>
        <span style={{ fontSize: 'var(--t-lead)', fontWeight: 600, color: 'var(--ink-900)', flex: 1, minWidth: 120 }}>
          
          {t('Codes-barres par taille')}
        </span>
        <span style={{ fontSize: 'var(--t-dense)', color: 'var(--ink-500)', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
          <Trans k='<1>{remplis}</1> renseignés' vars={{ remplis }} parts={[(x) => <span>{x}</span>]} /></span>
        <button
          type="button" onClick={amorcerScan}
          style={{
            height: 30, padding: '0 11px', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-xs)',
            background: 'var(--paper)', color: 'var(--ink-700)', fontSize: '12.5px', fontWeight: 600,
            whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer',
          }}
        >
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round">
            <path d="M3 5v14M6 5v14M9 5v14M13 5v14M17 5v14M20 5v14" />
          </svg>
          
          {t('Scanner')}
        </button>
        <button
          type="button" onClick={() => void toutGenerer()} disabled={liveCount}
          style={{
            height: 30, padding: '0 11px', border: '1px solid var(--teal-400)', borderRadius: 'var(--r-xs)',
            background: 'var(--paper)', color: 'var(--teal-700)', fontSize: '12.5px', fontWeight: 600,
            whiteSpace: 'nowrap', cursor: liveCount ? 'progress' : 'pointer', opacity: liveCount ? 0.6 : 1,
          }}
        >
          {liveCount ? t('Allocation…') : t('Tout générer')}
        </button>
      </div>

      {sizes.map((size) => {
        const rempli = (codes[size] ?? '').trim() !== '';
        return (
          <div key={size} style={{
            display: 'flex', alignItems: 'center', gap: 10, padding: '8px 14px',
            borderBottom: '1px solid var(--ink-100)',
          }}>
            <span style={{
              width: 44, flexShrink: 0, fontFamily: 'var(--font-mono)', fontSize: 'var(--t-lead)', fontWeight: 700,
              color: 'var(--ink-900)',
            }}>{size}</span>
            <input
              data-scansize={size}
              value={codes[size] ?? ''}
              onChange={(e) => onCode(size, e.target.value)}
              inputMode="numeric"
              placeholder={t('scanner ou générer')}
              aria-label={t('Code-barres de la taille {size}', { size })}
              style={{
                flex: 1, minWidth: 0, height: 34, padding: '0 10px', boxSizing: 'border-box',
                // ⚠️ La case renseignée se TEINTE : sur huit tailles, repérer les vides à l'œil est plus
                // sûr que relire huit suites de treize chiffres.
                border: `1px solid ${rempli ? 'var(--teal-400)' : 'var(--ink-200)'}`,
                background: rempli ? 'var(--teal-100)' : 'var(--paper)',
                borderRadius: 'var(--r-xs)', color: 'var(--ink-900)', fontSize: '12.5px',
                fontFamily: 'var(--font-mono)', outline: 'none',
              }}
            />
            <button
              type="button" onClick={() => void allouer([size])} disabled={liveCount}
              style={{
                height: 36, padding: '0 11px', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-xs)',
                background: 'var(--paper)', color: 'var(--ink-700)', fontSize: '12.5px', fontWeight: 600,
                flexShrink: 0, whiteSpace: 'nowrap', cursor: liveCount ? 'progress' : 'pointer',
                opacity: liveCount ? 0.6 : 1,
              }}
            >
              
              {t('Générer')}
            </button>
          </div>
        );
      })}

      <div style={{
        padding: '11px 14px', background: 'var(--ink-50)', fontSize: '11.5px', color: 'var(--ink-500)',
        lineHeight: 1.5,
      }}>
        
        {t('Une taille laissée vide n’est pas créée. Les codes générés utilisent la plage interne du magasin ; un code scanné du fournisseur est conservé tel quel.')}
      </div>
    </div>
  );
}

/**
 * CE QU'UNE FRAPPE DANS L'UN DES TROIS CHAMPS DE PRIX change aux deux autres — la règle de la maquette :
 * saisir un coef recalcule le PV depuis le PA (ou, sans PA, le PA depuis le PV) ; saisir un PV recalcule le coef ;
 * saisir un PA recalcule le PV quand un coef est posé, et le coef quand seul le PV l'est.
 *
 * ⚠️ LE PRIX EST CE QU'ON STOCKE, JAMAIS LE COEFFICIENT : il n'est qu'un outil de saisie.
 * ⚠️ TOUT LE CALCUL VIT DANS `prix-derive`, donc dans les cœurs : une copie locale ne tomberait pas au même
 *    centime que l'onglet Prix de la fiche, et le même coefficient donnerait deux prix selon l'écran.
 * ⚠️ UN CHAMP VIDE OU ILLISIBLE NE RECALCULE RIEN : écraser les deux autres sur une frappe en cours (« 2, »)
 *    effacerait ce que l'opérateur vient de poser.
 */
export function priceFieldPatch(
  field: 'pa' | 'coeff' | 'pv',
  value: string,
  d: Pick<Draft, 'pa' | 'coeff' | 'pv'>,
): Partial<Pick<Draft, 'pa' | 'coeff' | 'pv'>> {
  const next = { ...d, [field]: value };
  const read = (v: string, scale: number) => {
    const r = parseMinor(v, { scale });
    return r.state === 'ok' && r.minor > 0 ? r.minor : null;
  };
  const pa = read(next.pa, 4);
  const coeff = read(next.coeff, 2);
  const pv = read(next.pv, 2);
  const patch: Partial<Pick<Draft, 'pa' | 'coeff' | 'pv'>> = { [field]: value };
  const coeffFrom = () => coeffFromPrices(pv, pa);
  if (field === 'coeff' && coeff !== null) {
    if (pa !== null) {
      const ttc = ttcFromCoeff(pa, coeff);
      if (ttc !== null) patch.pv = toInputValue(ttc, 2);
    } else if (pv !== null) {
      const fine = purchaseFromCoeff(pv, coeff);
      if (fine !== null) patch.pa = toInputValue(fineEnCentimes(fine), 2);
    }
  } else if (field === 'pa' && pa !== null) {
    if (coeff !== null) {
      const ttc = ttcFromCoeff(pa, coeff);
      if (ttc !== null) patch.pv = toInputValue(ttc, 2);
    } else if (pv !== null) {
      const c = coeffFrom();
      if (c !== null) patch.coeff = toInputValue(c, 2);
    }
  } else if (field === 'pv' && pv !== null && pa !== null) {
    const c = coeffFrom();
    if (c !== null) patch.coeff = toInputValue(c, 2);
  }
  return patch;
}

/**
 * L'écran de fin — relevé l.1010.
 * ⚠️ « REVENIR À LA LISTE » EST L'ACTION PRINCIPALE (teal), « Créer le suivant » la secondaire. Je les
 * avais inversés : mettre la création en avant pousse à enchaîner, alors que le geste normal après une
 * création est d'aller VÉRIFIER ce qu'on vient de faire.
 */
function Fin({ nom, onSuivant, onListe }: { nom: string; onSuivant: () => void; onListe: () => void }) {
  return (
    <div style={{ padding: '34px 26px', textAlign: 'center' }}>
      <div style={{
        width: 52, height: 52, borderRadius: 'var(--r-pill)', background: 'var(--teal-100)',
        color: 'var(--teal-700)', display: 'flex', alignItems: 'center', justifyContent: 'center',
        margin: '0 auto 16px',
      }}>
        <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
          <path d="M20 6L9 17l-5-5" />
        </svg>
      </div>
      <div style={{ fontSize: 'var(--t-h3)', fontWeight: 700, color: 'var(--ink-900)' }}>{t('{nom} est créé', { nom })}</div>
      <p style={{ margin: '8px 0 0', fontSize: '13.5px', color: 'var(--ink-600)', lineHeight: 1.6 }}>
        <Trans k='Il descendra aux caisses au prochain contact. Les champs restants (poids, origine, douane…) sont marqués <1>N/C</1> et se complètent depuis sa fiche.' parts={[(x) => <strong style={{ color: 'var(--ink-900)' }}>{x}</strong>]} /></p>
      <div style={{ display: 'flex', gap: 10, justifyContent: 'center', marginTop: 22 }}>
        <button type="button" onClick={onSuivant} style={{
          height: 40, padding: '0 15px', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-ctrl)',
          background: 'var(--paper)', color: 'var(--ink-700)', fontSize: '13.5px', fontWeight: 600, cursor: 'pointer',
        }}>{t('Créer le suivant')}</button>
        <button type="button" onClick={onListe} style={{
          height: 40, padding: '0 17px', border: 'none', borderRadius: 'var(--r-ctrl)',
          background: 'var(--teal-500)', color: '#fff', fontSize: '13.5px', fontWeight: 700, cursor: 'pointer',
        }}>{t('Revenir à la liste')}</button>
      </div>
    </div>
  );
}

/** La grille du corps — `auto-fit minmax(220px,1fr)`, relevé l.1020. */
function Grid({ children }: { children: React.ReactNode }) {
  return <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 14 }}>{children}</div>;
}

/** Un champ. `large` = il occupe toute la rangée (`grid-column: 1/-1`). */
function FormRow({ label, children, large }: { label: string; children: React.ReactNode; large?: boolean }) {
  return (
    <label style={{ display: 'block', minWidth: 0, ...(large ? { gridColumn: '1/-1' } : null) }}>
      <span style={{
        display: 'block', fontSize: '11.5px', fontWeight: 500, color: 'var(--ink-500)', marginBottom: 5,
      }}>{label}</span>
      {children}
    </label>
  );
}

function Saisie({ value, onChange, placeholder, chiffres }: {
  value: string; onChange: (v: string) => void; placeholder?: string; chiffres?: boolean;
}) {
  // ⚠️ UN MONTANT D'EXEMPLE S'EFFACE QUAND ON ENTRE DANS LE CHAMP : « 24,00 » resté sous le curseur se lit
  //    comme un prix déjà saisi, et on tape à côté de lui (décision de l'utilisateur).
  const [focused, setFocused] = useState(false);
  return (
    <input
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      placeholder={chiffres && focused ? '' : placeholder}
      inputMode={chiffres ? 'decimal' : undefined}
      style={{
        width: '100%', height: 40, padding: '0 12px', boxSizing: 'border-box',
        border: '1px solid var(--ink-200)', borderRadius: 'var(--r-ctrl)',
        background: 'var(--paper)', color: 'var(--ink-900)', fontSize: '13.5px', outline: 'none',
        ...(chiffres ? { textAlign: 'right' as const, fontVariantNumeric: 'tabular-nums' } : null),
      }}
    />
  );
}

/** Une valeur qu'on ne saisit pas — même pavé violet que l'onglet Prix, même raison. */
function Fige({ valeur }: { valeur: string }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'flex-end', height: 40, padding: '0 12px',
      boxSizing: 'border-box', border: '1px dashed var(--violet-fg)', borderRadius: 'var(--r-ctrl)',
      background: 'var(--violet-bg)', color: 'var(--violet-fg)',
      fontSize: '13.5px', fontWeight: 700, fontVariantNumeric: 'tabular-nums',
    }}>{valeur}</div>
  );
}

/**
 * Un sélecteur de référentiel, avec création à la volée.
 * ⚠️ LES OPTIONS SONT CELLES DU MAGASIN, jamais une liste écrite ici : un référentiel inventé à
 * l'écran produirait un article rattaché à une famille que personne d'autre ne connaît.
 */
function Selecteur({ id, label, facette, valeur, onChange, offres, onCreateValue }: {
  id: string; label: string; facette: ArticleFacet;
  valeur: string; onChange: (v: string) => void; offres: ArticleFacets | null;
  /** Crée la valeur ET l'offre à la liste (`useOfferedFacets`) — rend ce que le serveur a retenu. */
  onCreateValue: (facet: ArticleFacet, value: string) => Promise<string>;
}) {
  // ⚠️ LE VIDE EST NEUTRE, ET AUCUN APPELANT NE PEUT LE CHANGER : « Choisir une saison… » accorde un
  //    article au mot de la plateforme, or le mot vient du magasin (« Millésime », « Pitouf ») et le
  //    vocabulaire n'a pas de genre — la phrase serait fausse dès le premier magasin qui renomme.
  const emptyLabel = t('À choisir');
  return (
    // ⚠️ PAS DE `<Champ>` AUTOUR — deux raisons, toutes deux constatées à l’écran :
    //   · `variant="field"` REND DÉJÀ son intitulé au-dessus du contrôle ; l’envelopper le doublait ;
    //   · `<Champ>` est un `<label>`, et TOUT clic dans un label ré-active son contrôle : le panneau
    //     se rouvrait donc à l’instant même où on le fermait, et devenait impossible à refermer.
    <div style={{ minWidth: 0 }}>
      <Picker
        id={`bo-new-${id}`}
        variant="field"
        label={label}
        clearLabel={emptyLabel}
        allLabel={emptyLabel}
        options={(offres?.values[facette] ?? []).map((v) => ({ value: v.value, label: v.value }))}
        onSearch={(size) => api.articleFacetSearch(facette, size).then((r) => r.map((o) => ({ value: o.value, label: o.value })))}
        onCreate={async (v) => {
          // ⚠️ LA CIBLE VIENT DE LA FACETTE, JAMAIS D'UNE PROP. Cet écran envoyait l'entité « season »,
          //    qui n'existe pas au registre : la porte répondait 422 et le bouton échouait en silence.
          const kept = await onCreateValue(facette, v);
          // ⚠️ « Valeur », et non « Désignation » : ce mot-là est AUSSI le libellé du nom de l'article
          //    (`fieldLabel('name')`), qu'un magasin renomme.
          showToast(t('Valeur créée — {label} : « {v} ».', { label, v: kept }));
          return kept;
        }}
        unavailable={!offres}
        value={valeur}
        onChange={(nv) => onChange(nv ?? '')}
      />
    </div>
  );
}

function Aide({ children }: { children: React.ReactNode }) {
  return <p style={{ margin: '8px 0 0', fontSize: '11.5px', color: 'var(--ink-400)', lineHeight: 1.5 }}>{children}</p>;
}
